/**
 * The driver's readout of someone else's journey, as a thing that ages and can be waved away.
 *
 * Both rules exist because the readout has no way of knowing it is wrong. It reports the last
 * thing the rider's phone said, and a phone that has gone quiet — underground, pocketed, asleep —
 * leaves it asserting something hours out of date in exactly the same voice as something true.
 */

/** How old the reading is, as a state the screen can wear. */
export type MonitorAge = "live" | "stale" | "dormant";

/** Long enough that a poll or two has been missed. */
export const MONITOR_STALE_MS = 5 * 60_000;

/**
 * Long enough that the reading has stopped being news and become a record.
 *
 * On 2026-09-08 the card said "on the train" for 65 minutes after the last fix; the minutes were
 * on screen but nothing else changed, so it read as current at a glance.
 */
export const MONITOR_DORMANT_MS = 30 * 60_000;

export function monitorAge(updatedAtMs: number, nowMs: number): MonitorAge {
	const age = nowMs - updatedAtMs;
	if (age >= MONITOR_DORMANT_MS) {
		return "dormant";
	}
	return age >= MONITOR_STALE_MS ? "stale" : "live";
}

/** A sideways drag across the readout. */
export interface MonitorDrag {
	deltaX: number;
	deltaY: number;
	width: number;
}

/** What the drag has earned: nothing, the hide button, or the hide itself. */
export type MonitorSwipe = "none" | "reveal" | "hide";

/** Far enough that a tap which drifted sideways does not count. */
const REVEAL_PX = 44;

/** Half the width, which is a deliberate action rather than a flick. */
const HIDE_FRACTION = 0.5;

/**
 * Leftwards only, and only when the drag is more sideways than vertical: the readout sits in a
 * scrolling column, so a thumb travelling down the page must never dismiss it.
 */
export function monitorSwipe({ deltaX, deltaY, width }: MonitorDrag): MonitorSwipe {
	const travelled = -deltaX;
	if (travelled <= 0 || travelled <= Math.abs(deltaY)) {
		return "none";
	}
	if (travelled >= Math.max(width * HIDE_FRACTION, REVEAL_PX * 2)) {
		return "hide";
	}
	return travelled >= REVEAL_PX ? "reveal" : "none";
}

/**
 * Whether a readout waved away is still hidden.
 *
 * Keyed on the reading it was hiding, so the next thing the rider's phone says brings it back.
 * Dismissing is "I have read this", not "leave me alone today".
 */
export function monitorHidden(dismissedAtMs: number | null, updatedAtMs: number): boolean {
	return dismissedAtMs != null && dismissedAtMs === updatedAtMs;
}

/** Who called today's pickup off, when one was. */
export type PickupCalledOff = "rider" | "driver" | null;

/**
 * Read the caller from a cancelled pickup.
 *
 * Only the rider's stand-down is dated, so a cancelled pickup with no date is the driver's own
 * doing — which is worth saying, because the two are the same row and look identical otherwise.
 */
export function pickupCalledOff(status: string | null | undefined, stoodDownAtMs: number | null | undefined): PickupCalledOff {
	if (status !== "cancelled") {
		return null;
	}
	return stoodDownAtMs != null ? "rider" : "driver";
}
